import React, { useState } from 'react';
import { CopyIcon, HourglassHighIcon, QuestionIcon, TrashIcon, ArrowsLeftRightIcon, CheckCircleIcon } from '@phosphor-icons/react';
import { PluginIDHashMap } from '~/utils/const';

import './index.less';

const DocHref = 'https://jcaigc-doc.feishu.cn/wiki/AczPwA3gpiQLh9ksICacxXd5nqh';

const WorkflowMigration: React.FC = () => {
  const [sourceWorkflow, setSourceWorkflow] = useState<string>('');
  const [targetWorkflow, setTargetWorkflow] = useState<string | object>('');
  const [isMigrating, setIsMigrating] = useState<boolean>(false);
  const [nodesCount, setNodesCount] = useState<number>(0);
  const [replacedCount, setReplacedCount] = useState<number>(0);
  const [copied, setCopied] = useState<boolean>(false);

  const handleSourceChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setSourceWorkflow(value);
    // 验证JSON格式和内容
    validateWorkflow(value);
  };

  const validateWorkflow = (workflowString: string) => {
    if (!workflowString) {
      setNodesCount(0);
      return;
    }

    try {
      // 尝试解析JSON字符串
      const parsedData = JSON.parse(workflowString);
      const jsonContent = parsedData?.json;
      // 检查一级key中是否有json字段
      // 检查是否包含nodes数组
      // 设置nodesCount为nodes数组的长度
      if (Array.isArray(jsonContent?.nodes)) {
        const len = jsonContent.nodes.length;
        if (len > 0) {
          setNodesCount(len);
          return;
        }
      }

      // 格式不符合要求：缺少json字段或nodes数组
      setNodesCount(0);
    } catch (error) {
      // 解析失败，JSON格式不正确
      setNodesCount(0);
    }
  };

  const handleClear = () => {
    setSourceWorkflow('');
    setTargetWorkflow('');
    setNodesCount(0);
  };

  const handleCopy = () => {
    if (targetWorkflow) {
      // 将targetWorkflow转换为字符串形式（如果是对象则转换为JSON字符串）
      const copyText =
        typeof targetWorkflow === 'object'
          ? JSON.stringify(targetWorkflow, null, 2)
          : targetWorkflow;
      navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const migrateWorkflow = async () => {
    try {
      // 使用正则表达式匹配所有 pluginID 的值
      const pluginIdRegex = /"pluginID":\s*"([^"]+)"/g;
      const pluginIds: string[] = [];
      let match;

      // 循环匹配所有符合条件的 pluginID
      while ((match = pluginIdRegex.exec(sourceWorkflow)) !== null) {
        const str = match[1];
        str && pluginIds.push(str);
      }

      let updatedWorkflow = sourceWorkflow;
      let replacedCount = 0;

      // 遍历 pluginIds 数组中的每个值
      pluginIds.forEach((pluginId) => {
        const newPluginId = PluginIDHashMap[pluginId];
        if (newPluginId) {
          const replaceRegex = new RegExp(`"${pluginId}"`, 'g');
          updatedWorkflow = updatedWorkflow.replace(replaceRegex, `"${newPluginId}"`);
          replacedCount++;
        }
      });
      return { updatedWorkflow, replacedCount };
    } catch (error) {
      return { updatedWorkflow: '', replacedCount: 0 };
    }
  };

  const handleMigrate = async () => {
    if (!sourceWorkflow || nodesCount === 0) return;

    setTargetWorkflow('');

    setIsMigrating(true);
    setReplacedCount(0); // 重置替换计数

    // 调用migrateWorkflow函数处理workflow
    const { updatedWorkflow, replacedCount } = await migrateWorkflow();

    try {
      // 尝试解析JSON字符串
      const parsedData = JSON.parse(updatedWorkflow);
      setTargetWorkflow(parsedData);
    } catch (error) {
      setTargetWorkflow(updatedWorkflow);
    }
    // 更新替换计数
    setReplacedCount(replacedCount);
    setIsMigrating(false);
  };

  return (
    <div className="workflow-migration-page zt-page h-full">
      <div className="zt-container zt-sm-container" style={{ position: 'relative', zIndex: 1 }}>
        <div className="migration-header">
          <h2 className="migration-title">
            剪映小助手工作流迁移工具
          </h2>
          <p className="migration-subtitle">将旧版插件工作流一键迁移到新版插件</p>
          {/* <a href={DocHref} target="_blank" rel="noopener noreferrer" className="migration-help-link inline-flex items-center gap-2">
           <QuestionIcon size={22} weight="bold" /> 如何迁移？查看详细说明
          </a> */}
        </div>

        <div className="migration-content">
          {/* 原工作流 */}
          <div className="migration-card source-card">
            <div className="card-header custom-flex" style={{justifyContent: 'space-between'}}>
              <div className="card-title">
                原工作流<span className="card-subtitle">粘贴工作流文本</span>
              </div>
              <button
                className="clear-button"
                onClick={handleClear}
                disabled={!sourceWorkflow}
                title="清空内容"
              >
                <TrashIcon size={14} weight="bold" />
                清空
              </button>
            </div>

            <textarea
              value={sourceWorkflow}
              onChange={handleSourceChange}
              className="code-editor"
              placeholder="粘贴工作流JSON数据..."
              spellCheck={false}
            />

            <div className="card-footer custom-flex" style={{justifyContent: 'space-between'}}>
              <div>
                {nodesCount > 0 && <span className="nodes-info">检测到 {nodesCount} 个节点</span>}
              </div>
              <button
                className="migrate-button"
                onClick={handleMigrate}
                disabled={!sourceWorkflow || nodesCount === 0 || isMigrating}
              >
                {isMigrating ? (
                  <>
                    <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="60" strokeDashoffset="20" />
                    </svg>
                    迁移中...
                  </>
                ) : (
                  <>
                    <ArrowsLeftRightIcon size={18} weight="bold" />
                    开始迁移
                  </>
                )}
              </button>
            </div>
          </div>

          {/* 迁移后工作流 */}
          <div className="migration-card target-card">
            <div className="card-header custom-flex" style={{justifyContent: 'space-between'}}>
              <div className="card-title">迁移后工作流</div>
              {targetWorkflow && (
                <button
                  className="copy-button"
                  onClick={handleCopy}
                  title="复制到剪贴板"
                >
                  {copied ? (
                    <CheckCircleIcon size={14} weight="bold" />
                  ) : (
                    <CopyIcon size={14} weight="bold" />
                  )}
                  {copied ? '已复制' : '复制'}
                </button>
              )}
            </div>

            {targetWorkflow ? (
              <>
                <div className="target-editor-container">
                  <textarea
                    value={
                      typeof targetWorkflow === 'object'
                        ? JSON.stringify(targetWorkflow, null, 2)
                        : targetWorkflow
                    }
                    className="code-editor target-editor filled"
                    readOnly
                    spellCheck={false}
                  />
                </div>
                <div className="card-footer">
                  <span className="nodes-info">
                    总节点：{nodesCount} · 迁移：{replacedCount} · 跳过：{nodesCount - replacedCount}
                  </span>
                </div>
              </>
            ) : (
              <div className="target-editor-container">
                <div className="migration-empty-state">
                  <div className="migration-empty-icon">
                    <HourglassHighIcon size={48} />
                  </div>
                  <div className="migration-empty-text">迁移后的工作流将显示在这里...</div>
                  <div className="migration-empty-subtext">等待迁移结果</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowMigration;
